import React, { useState, useCallback, useEffect, useRef } from "react";

import Button from "@cayuse/shared-react.components.button";
import DateSelector from "@cayuse/shared-react.components.date-selector";
import FormattedDate from "@cayuse/shared-react.components.formatted-date";
import Icon from "@cayuse/shared-react.components.icon";
import Popup from "@cayuse/shared-react.components.popup";
import { isFunction } from "@cayuse/shared-react.utils.type-checks";
import moment from "moment";
import PropTypes from "prop-types";

import CustomDropdown from "@cayuse/shared-react.custom-dropdown";
import { handleFocusTrap, FOCUSABLE_SELECTORS } from "utils";

import styles from "./styles.css";

const OPERATORS = [ "Is on", "Is between", "Is not between", "Is before", "Is on or before", "Is after", "Is on or after" ];

const getSearchExpression = (operator, value, endValue) => {
	switch(operator) {
	case OPERATORS[0]:
		return `"${value}"`;
	case OPERATORS[1]:
		return `["${value}" to "${endValue}"]`;
	case OPERATORS[2]:
		return `!["${value}" to "${endValue}"]`;
	case OPERATORS[3]:
		return `<${value}`;
	case OPERATORS[4]:
		return `<=${value}`;
	case OPERATORS[5]:
		return `>${value}`;
	case OPERATORS[6]:
		return `>=${value}`;
	default:
		return "";
	}
};

const parseSearchExpression = expression => {
	if (!expression || typeof expression !== "string") {
		return {
			operator: "",
			value: null,
			endValue: null
		};
	}

	const trimmedExpression = expression.trim();

	const notBetweenMatch = trimmedExpression.match(/^!\["(.+)"\s+to\s+"(.+)"\]$/);
	if (notBetweenMatch) {
		return {
			operator: OPERATORS[2],
			value: notBetweenMatch[1],
			endValue: notBetweenMatch[2]
		};
	}

	const betweenMatch = trimmedExpression.match(/^\["(.+)"\s+to\s+"(.+)"\]$/);
	if (betweenMatch) {
		return {
			operator: OPERATORS[1],
			value: betweenMatch[1],
			endValue: betweenMatch[2]
		};
	}

	const onMatch = trimmedExpression.match(/^"(.+)"$/);
	if (onMatch) {
		return {
			operator: OPERATORS[0],
			value: onMatch[1],
			endValue: null
		};
	}

	if (trimmedExpression.startsWith("<=")) {
		return {
			operator: OPERATORS[4],
			value: trimmedExpression.slice(2),
			endValue: null
		};
	}

	if (trimmedExpression.startsWith(">=")) {
		return {
			operator: OPERATORS[6],
			value: trimmedExpression.slice(2),
			endValue: null
		};
	}

	if (trimmedExpression.startsWith("<")) {
		return {
			operator: OPERATORS[3],
			value: trimmedExpression.slice(1),
			endValue: null
		};
	}

	if (trimmedExpression.startsWith(">")) {
		return {
			operator: OPERATORS[5],
			value: trimmedExpression.slice(1),
			endValue: null
		};
	}

	return {
		operator: "",
		value: null,
		endValue: null
	};
};

const DateFacet = ({
	text,
	query,
	onChange,
	onDelete,
}) => {
	const popupRef = useRef();
	const startDatePickerRef = useRef();
	const endDatePickerRef = useRef();
	const dateFacetTextRef = useRef();
	const [ selectedOperator, setSelectedOperator ] = useState(OPERATORS[0]);
	const [ isOpen, setOpen ] = useState(true);
	const [ startDate, setStartDate ] = useState();
	const [ endDate, setEndDate ] = useState();
	const [ searchText, setSearchText ] = useState();

	const returnFocusToNode = nodeId => {
		const node = document.getElementById(nodeId);

		if (node) {
			node.focus();
		}
	}

	const convertToText = (start = startDate, end = endDate, operator = selectedOperator) => {
		const value = moment(start).format("YYYY-MM-DD");
		let endValue = null;
		let text = (<> {operator?.toLowerCase()} <FormattedDate date={start} isUTC /></>);


		if ([ OPERATORS[1], OPERATORS[2] ].includes(operator)) {
			endValue = moment(end).format("YYYY-MM-DD");
			text = (<>{text} and <FormattedDate date={end} isUTC /></>);
		}

		setSearchText(text);
		setOpen(false);

		returnFocusToNode("editDateFacetPopupBtn")

		return { value, endValue };
	}

	const handleInput = () => {
		convertToText();
		const { value, endValue } = convertToText();
		const query = moment.utc(value).toISOString();
		if (isFunction(onChange)) {
			onChange({
				query,
				searchValue: getSearchExpression(selectedOperator, value, endValue)
			});
		}
	};

	const handleDelete = useCallback(() => {
		if (isFunction(onDelete)) {
			onDelete();
		}
	}, [ onDelete ]);

	useEffect(() => {
		if (query) {
			const { operator, value, endValue } = parseSearchExpression(query);
			setSelectedOperator(operator);
			setStartDate(value);
			if(endValue) {
				setEndDate(endValue);
			}
			convertToText(value, endValue, operator);
		}
	}, [  ]);

	// Auto-focus the first date input when the popup opens
	useEffect(() => {
		if (isOpen && popupRef.current) {
			// Use a short timeout to let Popup finish rendering into the DOM
			const timer = setTimeout(() => {
				const firstInput = popupRef.current.querySelector(FOCUSABLE_SELECTORS);
				if (firstInput) {
					firstInput.focus();
				}
			}, 0);

			return () => clearTimeout(timer);
		}
	}, [ isOpen ]);

	// Trap focus inside the popup while it is open
	useEffect(() => {
		if (!isOpen || !popupRef.current) { return; }

		const node = popupRef.current;

		node.addEventListener("keydown", e => {
			handleFocusTrap(e, node);
		});

		return () => {
			node.removeEventListener("keydown", e => {
				handleFocusTrap(e, node);
			});
		};
	}, [ isOpen ]);

	const operatorChanged = (event, { value }) => {
		setSelectedOperator(value);

		const operatorsDropdown = document.querySelector(".operatorsDropdown button");

		if (operatorsDropdown) {
			setTimeout(() => {
				operatorsDropdown.focus();
			}, 100);
		}
	};

	const onDateChange = (value, setDate) => {
		setDate(value);
	};

	// Open the calendar on Enter when a date input is focused
	const handleDateInputKeyDown = pickerRef => e => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();

			if (pickerRef.current) {
				pickerRef.current.setOpen(true);
			}
		}
	};

	const popupContent = () => (
		<div
			className={styles.popupContent}
			ref={popupRef}
			role="application"
			aria-label={`${dateFacetTextRef?.current?.innerText ? dateFacetTextRef.current.innerText : "Date filter"} edit popup`}
		>
			<CustomDropdown
				options={OPERATORS.map((option, index) => ({ key: index, text: option, value: option }))}
				onChange={operatorChanged}
				value={selectedOperator}
				customClassname="operatorsDropdown"
				isCustomized
				isCustomStyling
				isCustomOnChange
				ariaLabel="Operators"
				label="Select operator..."
			/>
			<span className={styles.screenReaderOnly} id="startDatePicker-label">{[ OPERATORS[1], OPERATORS[2] ].includes(selectedOperator) ? "Select start date" : `Select ${selectedOperator} date`}</span>
			<DateSelector
				className={styles.datePicker}
				onChange={value => {
					onDateChange(value, setStartDate)
				}}
				selectedDate={startDate}
				ref={startDatePickerRef}
				preventOpenOnFocus
				onKeyDown={handleDateInputKeyDown(startDatePickerRef)}
				ariaDescribedBy="startDatePicker-label"
				onCalendarClose={() => {
					if (startDatePickerRef.current) {
						setTimeout(() => {
							startDatePickerRef.current.input.focus();
						}, 100);
					}
				}}
			/>
			<span className={styles.screenReaderOnly} id="endDatePicker-label">Select end date</span>
			{[ OPERATORS[1], OPERATORS[2] ].includes(selectedOperator) && <DateSelector
				className={styles.datePicker}
				onChange={value => {
					onDateChange(value, setEndDate)
				}}
				selectedDate={endDate}
				ref={endDatePickerRef}
				preventOpenOnFocus
				onKeyDown={handleDateInputKeyDown(endDatePickerRef)}
				ariaDescribedBy="endDatePicker-label"
				onCalendarClose={() => {
					if (endDatePickerRef.current) {
						setTimeout(() => {
							endDatePickerRef.current.input.focus();
						}, 100);
					}
				}}
			/>}
			<div className={styles.buttonGroup}>
				<Button
					onClick={handleInput}
					primary
					tooltip={(endDate && startDate) && moment(endDate).isBefore(moment(startDate)) && "End date must be after start date"}
					disabled={
						!startDate ||
						([ OPERATORS[1], OPERATORS[2] ].includes(selectedOperator) && (!endDate || moment(endDate).isBefore(moment(startDate))))}
				>Apply</Button>
				<Button
					onClick={() => {
						setOpen(false)
						returnFocusToNode("editDateFacetPopupBtn");
					}}
					secondary
				>Cancel</Button>
			</div>
		</div>
	// eslint-disable-next-line react-hooks/exhaustive-deps
	);

	return (
		<div
			data-component="FacetedSearchBar\DateFacet"
			className={styles.facetLabelInput}
		>
			<div ref={dateFacetTextRef} data-testid="date-facet-text">
				{ text }
				{ searchText }
			</div>

			<Popup
				flowing={true}
				content={popupContent}
				eventsEnabled={true}
				on="click"
				open={isOpen}
				trigger={(
					<button
						aria-label={`Edit ${dateFacetTextRef?.current?.innerText ? dateFacetTextRef.current.innerText : text} filter`}
						id="editDateFacetPopupBtn"
						className={styles.editPopupBtn}
						onClick={e => e.stopPropagation()}
					>
						Edit
					</button>
				)}
				id={styles.dateFilterPopup}
				onClose={() => { 
					setOpen(false);
					returnFocusToNode("editDateFacetPopupBtn");
				}}
				onOpen={() => setOpen(true)}
				className={styles.popup}
				position="bottom left"
				onClick={e => e.stopPropagation()}
			/>

			<button
				data-testid='remove-date-filter-btn'
				aria-label={`Remove ${text} filter`}
				onClick={e => {
					e.stopPropagation();
					handleDelete();
				}}
				onKeyDown={e => {
					if (e.key === "Enter") {
						e.stopPropagation();
						handleDelete();
					}
				}}
				className={styles.deleteButton}
			>
				<Icon className={styles.icon} name="times" size="smaller" removeMargin />
			</button>
		</div>
	);
};

DateFacet.propTypes = {
	text: PropTypes.string,
	query: PropTypes.string,
	onChange: PropTypes.func,
	onClick: PropTypes.func,
	onDelete: PropTypes.func,
	shouldFocus: PropTypes.bool,
	useTime: PropTypes.oneOfType([
		PropTypes.bool, PropTypes.string
	])
};

DateFacet.defaultProps = {
	onClick: () => {},
	useTime: false
};

export default DateFacet;
